
import { Route } from '@angular/router';
import { authGuard, nonAuthGuard } from '@rapider/authentication';
import { activeProjectGuard } from '@rapider/workspaces';

import { provideAiChatFeature } from '@rapider/ai-chat';
import { provideProjectDataFeature } from '@rapider/project-data';
import { provideDatabaseFeature } from '@rapider/database';

export const appRoutes: Route[] = [
  /* =========================================
     PUBLIC AUTH ROUTES (No Layout Wrapper)
     Protected by nonAuthGuard: Logged-in users cannot access these.
     ========================================= */
  {
    path: 'auth/login',
    title: 'Login - Rapider AI',
    canActivate: [nonAuthGuard],
    loadComponent: () =>
      import('@rapider/authentication').then((c) => c.LoginComponent),
  },
  {
    path: 'auth/register',
    title: 'Register - Rapider AI',
    canActivate: [nonAuthGuard],
    loadComponent: () =>
      import('@rapider/authentication').then((c) => c.RegisterComponent),
  },
  {
    path: 'auth/forgot-password',
    title: 'Forgot Password - Rapider AI',
    canActivate: [nonAuthGuard],
    loadComponent: () =>
      import('@rapider/authentication').then((c) => c.ForgotPasswordComponent),
  },
  {
    path: 'auth/social-media-login',
    title: 'Authenticating - Rapider AI',
    canActivate: [nonAuthGuard],
    loadComponent: () =>
      import('@rapider/authentication').then(
        (c) => c.SocialMediaLoginComponent,
      ),
  },

  /* =========================================
     AUTHENTICATED ROUTES (Wrapped in Layout Shell)
     Protected by authGuard: Only logged-in users can access these.
     ========================================= */
  {
    path: '',
    canActivate: [authGuard, activeProjectGuard],
    // Providers for the entire authenticated section - includes the AI Chat NgRx state/effects
    providers: [
      provideAiChatFeature(),
      provideProjectDataFeature(),
      provideDatabaseFeature(),
    ],
    loadComponent: () =>
      import('@rapider/authenticated-layout').then(
        (c) => c.AuthenticatedLayoutComponent,
      ),
    children: [
      {
        path: 'chat',
        title: 'Rapider AI',
        loadComponent: () =>
          import('@rapider/database').then((m) => m.WorkspaceCanvasComponent),
      },
      {
        path: 'dashboard',
        title: 'Dashboard - Rapider AI',
        loadComponent: () =>
          import('@rapider/main-dashboard').then(
            (m) => m.MainDashboardComponent,
          ),
      },
      {
        path: 'data-operations-log',
        title: 'Data Operations Log - Rapider AI',
        loadComponent: () =>
          import('@rapider/database').then(
            (m) => m.DataInsightTimelineComponent,
          ),
      },

      {
        path: 'data-insight',
        title: 'Data Insight - Rapider AI',
        loadComponent: () =>
          import('@rapider/database').then((m) => m.DataInsightComponent),
      },

      /* * CRITICAL FIX: Redirect legacy dashboard navigations to chat.
       * Prevents infinite loops when auth guards or workspace services attempt to route to the old dashboard path.
       */
      {
        path: 'dashboard',
        redirectTo: 'chat',
        pathMatch: 'full',
      },

      /* =========================================================
         APP STORE ROUTES
         ========================================================= */
      {
        path: 'applications',
        title: 'My Applications - Rapider AI',
        loadComponent: () =>
          import('@rapider/app-store').then(
            (m) => m.InstalledApplicationsComponent,
          ),
      },
      {
        path: 'app-store',
        title: 'App Store - Rapider AI',
        loadComponent: () =>
          import('@rapider/app-store').then((m) => m.AppStoreListComponent),
      },
      {
        path: 'app-store/:appId',
        title: 'App Details - Rapider AI',
        loadComponent: () =>
          import('@rapider/app-store').then((m) => m.AppStoreDetailComponent),
      },

      /* =========================================================
         AI FUNCTION CALLS ROUTES
         ========================================================= */
      {
        path: 'ai-function-calls',
        title: 'AI Functions - Rapider AI',
        data: { viewMode: 'canvas-only' },
        loadComponent: () =>
          import('@rapider/ai-function-calls').then(
            (m) => m.AiFunctionCallsListComponent,
          ),
      },
      {
        path: 'ai-function-calls/detail/:id',
        title: 'AI Function Details - Rapider AI',
        data: { viewMode: 'canvas-only' },
        loadComponent: () =>
          import('@rapider/ai-function-calls').then(
            (m) => m.AiFunctionCallDetailComponent,
          ),
      },

      /* =========================================================
         WORKSPACE FLOW ROUTES
         ========================================================= */
      {
        path: 'definitions',
        title: 'Project Definitions - Rapider AI',
        data: { viewMode: 'canvas-only' },
        loadComponent: () =>
          import('@rapider/project-definitions').then(
            (m) => m.DefinitionsListComponent,
          ),
      },
      {
        path: 'definitions/:id',
        title: 'Edit Definition - Rapider AI',
        data: { viewMode: 'canvas-only' },
        loadComponent: () =>
          import('@rapider/project-definitions').then(
            (m) => m.DefinitionEditComponent,
          ),
      },
      {
        path: 'select-workspace',
        title: 'Select Workspace - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/workspaces').then((m) => m.WorkspaceSelectComponent),
      },
      {
        path: 'create-workspace',
        title: 'Create Workspace - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/workspaces').then((m) => m.WorkspaceCreateComponent),
      },
      {
        path: 'workspace-settings',
        title: 'Workspace Settings - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/workspaces').then(
            (m) => m.WorkspaceSettingsComponent,
          ),
      },

      /* =========================================================
         ONBOARDING / SETUP ROUTES
         ========================================================= */
      {
        path: 'onboarding',
        title: 'Onboarding - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/onboarding').then((m) => m.OnboardingComponent),
      },
      {
        path: 'data-templates',
        title: 'Data Templates - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/onboarding').then((m) => m.DataTemplatesComponent),
      },
      {
        path: 'data-templates/:id',
        title: 'Template Details - Rapider AI',
        loadComponent: () =>
          import('@rapider/onboarding').then(
            (m) => m.DataTemplatesPreviewComponent,
          ),
        data: { viewMode: 'canvas-only-not-resizable' },
      },
      {
        path: 'data-templates/:id/integrate',
        title: 'Integrate Template - Rapider AI',
        loadComponent: () =>
          import('@rapider/onboarding').then(
            (m) => m.ProjectModelLibraryIntegrationComponent,
          ),
        data: { viewMode: 'canvas-only-not-resizable' },
      },
      {
        path: 'import-data',
        title: 'Import Data - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/onboarding').then((m) => m.ImportDataComponent),
      },
      {
        path: 'ai-setup',
        title: 'Start with AI - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/onboarding').then((m) => m.AiSetupComponent),
      },

      /* =========================================================
         DATABASE ROUTES
         ========================================================= */
      {
        path: 'database',
        title: 'Database - Rapider AI',
        data: { collapseSidebar: true },
        loadComponent: () =>
          import('@rapider/database').then((m) => m.DatabaseListComponent),
      },
      {
        // NEW HEADLESS ROUTE ADDED HERE FOR SEMANTIC DATABASE LINKING
        path: 'navigate-to-project-model-with-name/:projectModelName',
        loadComponent: () =>
          import('@rapider/database').then(
            (m) => m.NavigateToProjectModelComponent,
          ),
      },
      {
        path: 'database/:id',
        title: 'Model Details - Rapider AI',
        loadComponent: () =>
          import('@rapider/database').then(
            (m) => m.ProjectModelDetailComponent,
          ),
      },

      /* =========================================================
         SPACES / KNOWLEDGE BASE ROUTES
         ========================================================= */
      {
        path: 'spaces',
        title: 'Spaces - Rapider AI',
        data: { viewMode: 'canvas-only' },
        loadComponent: () =>
          import('@rapider/spaces').then((m) => m.SpacesHomeComponent),
      },
      {
        path: 'spaces/save-to-page',
        title: 'Save to Space - Rapider AI',
        data: { viewMode: 'split' },
        loadComponent: () =>
          import('@rapider/spaces').then((m) => m.SpacePageSelectorComponent),
      },
      {
        path: 'spaces/:spaceKey',
        title: 'Space Details - Rapider AI',
        data: { viewMode: 'split' },
        loadComponent: () =>
          import('@rapider/spaces').then((m) => m.SpaceDetailComponent),
      },
      {
        path: 'spaces/:spaceKey/:pageKey',
        title: 'Space Page - Rapider AI',
        data: { viewMode: 'split' },
        loadComponent: () =>
          import('@rapider/spaces').then((m) => m.SpacePageComponent),
      },
      /* =========================================================
         DYNAMIC APPLICATION VIEWER ROUTE
         ========================================================= */
      {
        path: 'app/:appId',
        title: 'Application Viewer - Rapider AI',
        loadComponent: () =>
          import('@rapider/template-renderer').then(
            (m) => m.DynamicAppViewerComponent,
          ),
      },
      {
        path: 'app/:appId/:pageKey',
        title: 'Application Viewer - Rapider AI',
        loadComponent: () =>
          import('@rapider/template-renderer').then(
            (m) => m.DynamicAppViewerComponent,
          ),
      },

      /* =========================================================
         WORKFLOW EXECUTION ROUTES
         ========================================================= */
      {
        path: 'workflow-execution',
        title: 'Workflow Execution - Rapider AI',
        data: { viewMode: 'split' },
        loadComponent: () =>
          import('@rapider/ai-workflow').then(
            (m) => m.UiWorkflowExecutionComponent,
          ),
      },

      /* =========================================================
         SHOWCASE ROUTES
         ========================================================= */
      {
        path: 'components-showcase',
        title: 'UI Components Showcase - Rapider AI',
        loadComponent: () =>
          import('@rapider/components-showcase').then(
            (m) => m.ComponentsShowcaseComponent,
          ),
      },

      /* =========================================================
         CONTENT EDITOR / APP BUILDER ROUTES
         ========================================================= */
      {
        path: 'app-builder',
        title: 'App Builder - Rapider AI',
        data: { viewMode: 'canvas-only' },
        loadComponent: () =>
          import('@rapider/content-editor').then((m) => m.AppBuilderComponent),
      },
      {
        path: 'app-builder/create',
        title: 'Create App - Rapider AI',
        data: { viewMode: 'canvas-only' },
        loadComponent: () =>
          import('@rapider/content-editor').then((m) => m.AppCreateComponent),
      },
      {
        path: 'app-builder/:appId',
        title: 'App Builder - Rapider AI',
        data: { viewMode: 'canvas-only' },
        loadComponent: () =>
          import('@rapider/content-editor').then((m) => m.AppBuilderComponent),
      },
      {
        path: 'app-builder/:appId/page/:pageId/design-pipeline',
        title: 'AI Design Pipeline - Rapider AI',
        data: { viewMode: 'canvas-only', collapseSidebar: true }, // Fullscreen editor mode
        loadComponent: () =>
          import('@rapider/content-editor').then(
            (m) => m.PageDesignPipelineComponent,
          ),
      },
      {
        path: 'app-builder/:appId/page/:pageId/ui-designer',
        data: { viewMode: 'canvas-only', collapseSidebar: true }, // Fullscreen editor mode
        loadComponent: () =>
          import('@rapider/content-editor').then(
            (m) => m.DragDropBuilderComponent,
          ),
      },
      {
        path: 'app-builder/:appId/page/:pageId/review',
        title: 'Page Review - Rapider AI',
        data: { viewMode: 'canvas-only', collapseSidebar: true }, // Fullscreen review mode
        loadComponent: () =>
          import('@rapider/content-editor').then(
            (m) => m.InteractivePageReviewComponent,
          ),
      },

      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },

  /* =========================================
     FALLBACK ROUTE
     ========================================= */
  {
    path: '**',
    redirectTo: 'auth/login',
  },
];